import { Icon } from "@blueprintjs/core"
import Divider from "components/Atoms/Divider"
import Note from "components/Atoms/Note"
import React from "react"
import classNames from "utils/classNames"
import { getId } from "utils/custom.utils"
import DCAreasServed from "./basic-details/DCAreasServed"
import DCBasicDetails from "./basic-details/DCBasicDetails"
import BillingDetails from "./BillingDetails"
import styles from "./dc.module.scss"
import useDCStore, { DCRegistrationFormModelType } from "./dc.store"
import DCFormTab from "./DCFormTab"
import DCMutation from "./DCMutation"
import DCRegistrationActions from "./DCRegistrationActions"
import DeliveryCharges from "./DeliveryCharges"
import LicenseDetails from "./licence-details/LicenseDetails"
import OperatingHours from "./OperatingHours"
import PaymentMethods from "./payment-methods/PaymentMethods"
import StockPreferences from "./StockPreferences"
import StockTake from "./StockTake"
import StorageLocations from "./StorageLocations"
import DCSurveyAreaList from "./survey-area/DCSurveyAreaList"

export type FormTabType = {
  title: string
  id?: string
  formKey?: keyof DCRegistrationFormModelType
  component?: any
  children?: FormTabType[]
}
export const dcFormTabs: FormTabType[] = [
  {
    title: "Primary Details",
    id: "primaryDetails",
    children: [
      {
        title: "Basic Details",
        id: "basic-details",
        formKey: "basicDetails",
        component: DCBasicDetails,
      },
      {
        title: "Areas Served",
        id: "areas-served",
        formKey: "areasServed",
        component: DCAreasServed,
      },
    ],
  },
  {
    title: "Secondary Details",
    children: [
      {
        title: "Storage Locations",
        id: "storage-locations",
        component: StorageLocations,
      },
      {
        title: "Billing Details",
        id: "billing-details",
        component: BillingDetails,
        formKey: "billingDetails",
      },
      {
        title: "Licence Details",
        id: "licence-details",
        component: LicenseDetails,
        formKey: "licenseDetails",
      },
      {
        title: "Payment Methods",
        id: "payment-methods",
        component: PaymentMethods,
        formKey: "paymentMethods",
      },
    ],
  },
  {
    title: "Configurations",
    children: [
      {
        title: "Survey Areas",
        id: "survey-areas",
        component: DCSurveyAreaList,
        formKey: "surveyAreas",
      },
      {
        title: "Operational Slots",
        id: "operational-slots",
        component: OperatingHours,
        formKey: "operationalSlots",
      },
      {
        title: "Delivery Charges",
        id: "delivery-charges",
        component: DeliveryCharges,
        formKey: "deliveryCharges",
      },
      {
        title: "Stock-take Timings",
        id: "stock-take-timings",
        component: StockTake,
      },
      {
        title: "Stock Broadcast Preferences",
        id: "stock-broadcast-preferences",
        component: StockPreferences,
      },
    ],
  },
]
const DCFormSections = () => {
  const { name, assignedCpc, color, id, archived } = useDCStore(store => {
    const details = store.state.basicDetails
    return {
      archived: details.archive,
      id: details.id,
      color: details.colorCode?.id || details.colorCode,
      name: details.name,
      assignedCpc: details.assignedCpc,
      allowedDepartments: details?.allowedDepartments?.map(getId),
    }
  })

  return (
    <div className={classNames(styles.card, styles.tabs)}>
      <header className="flex justify-between">
        <div className="flex align-baseline">
          <div
            className="d-center rounded h-8 w-8 mr-2"
            style={{ background: color || "#ccc", opacity: archived && 0.5 }}
          >
            {archived ? <Icon icon="disable" color="white" /> : !color && "!"}
          </div>
          <div>
            <h4 className={archived && "opacity-50"}>{name || "New DC"}</h4>
            <h5
              className={classNames(
                "flex items-center leading-tight",
                archived && "opacity-50"
              )}
            >
              {assignedCpc?.id && (
                <svg
                  className="mr-1"
                  width="12"
                  height="12"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    opacity="0.2"
                    d="M13.5 13.5V8.5H10.5L6.5 5.5V8.5L2.5 5.5V13.5H13.5Z"
                    fill="#656565"
                  />
                  <path
                    d="M5 11H6.75"
                    stroke="#656565"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M9.25 11H11"
                    stroke="#656565"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M13.5 13.5V8.5H10.5L6.5 5.5V8.5L2.5 5.5V13.5"
                    stroke="#656565"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M1 13.5H15"
                    stroke="#656565"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M13.5 8.5L12.5625 1.93125C12.5459 1.81195 12.4869 1.70264 12.3962 1.62341C12.3055 1.54419 12.1892 1.50036 12.0688 1.5H10.9312C10.8108 1.50036 10.6945 1.54419 10.6038 1.62341C10.5131 1.70264 10.4541 1.81195 10.4375 1.93125L9.59375 7.825"
                    stroke="#656565"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
              {(assignedCpc?.id && assignedCpc?.name) || "No CPC"}
            </h5>
          </div>
        </div>
        {id && <DCRegistrationActions />}
      </header>
      <Divider />
      {dcFormTabs.map(tab => (
        <div key={tab.title} className={styles.section}>
          <h6
            className={!id && tab.id !== "primaryDetails" ? "text-muted" : null}
          >
            {tab.title}
          </h6>
          {tab.children.map(child => (
            <DCFormTab tab={child} key={child.id} parentId={tab.id} />
          ))}
          {!id && tab.id === "primaryDetails" && (
            <Note className="mt-3" intent="danger">
              <span className="font-700">Note: </span> You will have to “Create
              DC” first, then add further information
            </Note>
          )}
        </div>
      ))}
      <Divider />
      <footer>
        <DCMutation />
      </footer>
    </div>
  )
}

export default DCFormSections
